import React, { useState, useRef, useEffect, useCallback } from 'react';
import { AreaChart, Area, XAxis, YAxis, ResponsiveContainer, Tooltip } from 'recharts';
import { Card, CardHeader, MetricValue, StackedProgressBar, LegendItem } from '../components/Card';
import { MiniLineChart, CustomTooltip } from '../components/Charts';
import DateRangePicker from '../components/DateRangePicker';
import AIInsightsCard from '../components/AIInsightsCard';
import { ChevronDownIcon, CheckIcon, CloseIcon, PlusIcon, CardIcon, CashIcon, TransferIcon, FilterIcon, InfoIcon } from '../icons/FinanceIcons';
import { getDatePresetLabel } from '../data/mockData';
import { financeService } from '../../../_services/finance.service';
import useWindowSize from '../../new-service/useWindowSize';
import { siteColour, switchToSite } from '../../../_services/sites.service';
import { firstRefundTotals, netAfterRefunds, refundCountLabel, rowRefunds, minusMoney } from '../../refunds/refundLogic';
// Format a date string for chart labels.
// Handles both daily ("2026-02-17") and weekly ("2026-W08") formats.
const formatChartLabel = (dateStr) => {
  if (!dateStr) return '';

  // Weekly format from backend: "YYYY-WXX" (MongoDB %U, Sunday-start week)
  const weekMatch = dateStr.match(/^(\d{4})-W(\d{1,2})$/);
  if (weekMatch) {
    const year = parseInt(weekMatch[1]);
    const weekNum = parseInt(weekMatch[2]);
    // %U week: Week 00 contains days before first Sunday. Week 01 starts on first Sunday.
    const jan1 = new Date(year, 0, 1);
    const jan1Day = jan1.getDay(); // 0=Sun
    const daysToFirstSunday = jan1Day === 0 ? 0 : (7 - jan1Day);
    const weekStart = new Date(year, 0, 1 + daysToFirstSunday + (weekNum > 0 ? (weekNum - 1) * 7 : 0));
    if (weekNum === 0) weekStart.setDate(1); // week 0 = start of year
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);
    const startLabel = weekStart.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    const endLabel = weekEnd.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    return `${startLabel} - ${endLabel}`;
  }

  // Hourly format from backend: "YYYY-MM-DDTHH"
  const hourMatch = dateStr.match(/^(\d{4}-\d{2}-\d{2})T(\d{2})$/);
  if (hourMatch) {
    return `${hourMatch[2]}:00`;
  }

  // Daily format: "YYYY-MM-DD"
  const dateObj = new Date(dateStr + 'T00:00:00');
  if (isNaN(dateObj.getTime())) return dateStr;
  return dateObj.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};


// Helper to get color for payment method
const getPaymentColor = (method) => {
  const colors = {
    'Beu Terminal': '#007EA0',
    'Payment Link': '#3b82f6',
    'Cash': '#14b8a6',
    'Voucher': '#f59e0b',
    'Card on File': '#8b5cf6',
    'Klarna': '#FFB3C7',
    'Clearpay': '#B2FCE4',
    'Card': '#0ea5e9',
    'Other': '#ef4444',
    'Loyalty reward': '#ec4899'
  };
  return colors[method] || '#737373';
};

// Helper to format date as YYYY-MM-DD
const formatDateForApi = (date) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Helper to get date range from preset
const getDateRangeFromPreset = (preset) => {
  const now = new Date();
  let startDate, endDate;

  switch (preset) {
    case 'today':
      startDate = endDate = new Date(now);
      break;
    case 'yesterday':
      startDate = endDate = new Date(now.setDate(now.getDate() - 1));
      break;
    case 'last7':
      endDate = new Date();
      startDate = new Date(new Date().setDate(new Date().getDate() - 7));
      break;
    case 'last4weeks':
      endDate = new Date();
      startDate = new Date(new Date().setDate(new Date().getDate() - 28));
      break;
    case 'last6months':
      endDate = new Date();
      startDate = new Date(new Date().setMonth(new Date().getMonth() - 6));
      break;
    case 'last12months':
      endDate = new Date();
      startDate = new Date(new Date().setFullYear(new Date().getFullYear() - 1));
      break;
    case 'wtd':
      endDate = new Date();
      startDate = new Date(now.setDate(now.getDate() - now.getDay()));
      break;
    case 'mtd':
      endDate = new Date();
      startDate = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
      break;
    case 'qtd':
      endDate = new Date();
      const quarter = Math.floor(new Date().getMonth() / 3);
      startDate = new Date(new Date().getFullYear(), quarter * 3, 1);
      break;
    case 'ytd':
      endDate = new Date();
      startDate = new Date(new Date().getFullYear(), 0, 1);
      break;
    default:
      endDate = new Date();
      startDate = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
  }

  return { startDate, endDate };
};

// Booking status options for the filter
const bookingStatusOptions = [
  { id: 'all', label: 'All Bookings' },
  { id: 'completed', label: 'Completed', statuses: [4] },
  { id: 'confirmed', label: 'Confirmed', statuses: [2] },
  { id: 'cancelled', label: 'Cancelled', statuses: [3] },
  { id: 'noshow', label: 'No Shows', statuses: [6] }
];

// Chart status filter options (used to filter gross revenue chart by booking status)
const chartStatusOptions = [
  { id: 'completed', label: 'Complete', bookingStatus: '4', color: '#059669' },
  { id: 'booked', label: 'Booked', bookingStatus: '1', color: '#3b82f6' },
  { id: 'noshow', label: 'No Show', bookingStatus: '6', color: '#f59e0b' },
  { id: 'cancelled', label: 'Cancelled', bookingStatus: '3', color: '#ef4444' },
];

// Chart type filter options
const chartTypeOptions = [
  { id: 'service', label: 'Services' },
  { id: 'product', label: 'Products' },
  { id: 'voucher', label: 'Voucher' },
  { id: 'commission', label: 'Commission' },
];

// Chart payment method filter options
const chartPaymentMethodOptions = [
  { id: 3, label: 'Cash' },
  { id: 5, label: 'Beu Card Readers' },
  { id: 2, label: 'Other Card Readers' },
  { id: 4, label: 'Other' },
  { id: 10, label: 'Saved Card' },
  { id: 11, label: 'Vouchers' },
  { id: 16, label: 'Loyalty Rewards' },
  { id: 12, label: 'Quick Pay (Link & QR)' },
  { id: 13, label: 'Klarna' },
  { id: 14, label: 'Clearpay' },
];

// What sits around gross revenue (unactioned bookings, loyalty rewards, net
// collected, tax), as a short label / amount list. As running sentences
// these broke mid-phrase into ragged columns on a phone. On desktop
// (layout="strip") they sit side by side in equal columns instead.
const RevenueBreakdown = ({ rows, layout = 'list' }) => {
  if (!rows.length) return null;
  if (layout === 'strip') {
    return (
      <dl style={{ display: 'grid', gridTemplateColumns: `repeat(${rows.length}, minmax(0, 1fr))`, margin: '20px 0 0', padding: '16px 0 0', borderTop: '1px solid #f0f0f0' }}>
        {rows.map((row, i) => (
          <div key={row.label} title={row.help} style={{ padding: i ? '0 16px' : '0 16px 0 0', borderLeft: i ? '1px solid #f0f0f0' : 'none', cursor: row.help ? 'help' : 'default', minWidth: 0 }}>
            <dt style={{ margin: '0 0 4px', fontSize: '12px', fontWeight: 400, color: '#737373', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {row.label}
              {row.note && <span style={{ color: '#9ca3af' }}> · {row.note}</span>}
            </dt>
            <dd style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: row.colour || '#1a1a1a', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{row.value}</dd>
          </div>
        ))}
      </dl>
    );
  }
  return (
    <dl style={{ margin: '14px 0 0', padding: '4px 0 0', borderTop: '1px solid #f0f0f0', maxWidth: '420px' }}>
      {rows.map((row) => (
        <div key={row.label} title={row.help} style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '12px', padding: '7px 0', fontSize: '13px', cursor: row.help ? 'help' : 'default' }}>
          <dt style={{ color: '#737373', margin: 0, fontWeight: 400 }}>
            {row.label}
            {row.note && <span style={{ color: '#9ca3af' }}> · {row.note}</span>}
          </dt>
          <dd style={{ margin: 0, fontWeight: 600, color: row.colour || '#1a1a1a', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{row.value}</dd>
        </div>
      ))}
    </dl>
  );
};

// Overview View Component
const RevenueSplitCards = ({ summary, stylistName, formatCurrency, inline = false }) => {
  const card = { flex: '1 1 0', minWidth: '120px', padding: '10px 14px', borderRadius: '10px', backgroundColor: '#fafafa', border: '1px solid #ececec' };
  const label = { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: '600', color: '#737373', margin: '0 0 4px 0', textTransform: 'uppercase', letterSpacing: '0.05em' };
  const dot = (c) => ({ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: c, flexShrink: 0 });
  const value = { fontSize: '17px', fontWeight: '600', color: '#1a1a1a', margin: 0 };
  return (
    <div style={{ marginTop: inline ? 0 : '14px' }}>
      <p style={{ fontSize: '12px', fontWeight: '500', color: '#737373', margin: '0 0 8px 0' }}>Revenue split</p>
      <div style={{ display: 'flex', gap: '10px' }}>
        <div style={card}>
          <p style={label}><span style={dot('#007EA0')} />To Business</p>
          <p style={value}>{formatCurrency(summary.salonRevenue)}</p>
        </div>
        <div style={card}>
          <p style={label}><span style={dot('#7c3aed')} />To Stylist</p>
          <p style={value}>{formatCurrency(summary.stylistRevenue)}</p>
          {stylistName && <p style={{ fontSize: '11px', color: '#9ca3af', margin: '3px 0 0 0' }}>{stylistName}</p>}
        </div>
      </div>
    </div>
  );
};

const OverviewView = ({ onViewCommission, staffFilter = 'all', staffList = [], salonIds = null, selectedSites = [] }) => {
  const { width } = useWindowSize();
  const isMobile = width <= 768;

  // Determine if salon has any self-employed staff for revenue breakdown
  const hasSEStaff = staffList.some(s => s.id !== 'all' && s.employmentTypeId === 2);
  const filteredStaffInfo = staffList.find(s => s.id === staffFilter);
  const isFilteredStaffSE = filteredStaffInfo?.employmentTypeId === 2;

  const [tipsExpanded, setTipsExpanded] = useState(false);
  const [bookingStatusFilter, setBookingStatusFilter] = useState('all');
  const [bookingStatusOpen, setBookingStatusOpen] = useState(false);

  // Chart-only filter state
  const [chartFiltersOpen, setChartFiltersOpen] = useState(false);
  const chartBarRef = useRef(null);
  const [chartBarSticky, setChartBarSticky] = useState(false);
  const [chartBarStickyTop, setChartBarStickyTop] = useState(57);
  const [chartStatusFilter, setChartStatusFilter] = useState([]);
  const [chartTypeFilter, setChartTypeFilter] = useState([]);
  const [chartPaymentMethodFilter, setChartPaymentMethodFilter] = useState([]);

  // Chart-specific date range state (independent from sticky bar)
  const [chartDateFilter, setChartDateFilter] = useState('today');
  const [chartDateStart, setChartDateStart] = useState(() => {
    const now = new Date(); return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  });
  const [chartDateEnd, setChartDateEnd] = useState(() => {
    const now = new Date(); return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  });
  const [chartGranularity, setChartGranularity] = useState('daily');
  const [chartCompareFilter, setChartCompareFilter] = useState('previous');
  const [chartDatePickerOpen, setChartDatePickerOpen] = useState(false);
  const [chartGranularityOpen, setChartGranularityOpen] = useState(false);
  const [chartCompareOpen, setChartCompareOpen] = useState(false);
  const [chartRevenueData, setChartRevenueData] = useState([]);
  const [chartRevenueSummary, setChartRevenueSummary] = useState({ current: 0, previous: 0, change: 0, salonRevenue: 0, stylistRevenue: 0, pipelineRevenue: 0, pipelineCount: 0, loyaltyComps: 0 });
  const [chartRevenueLoading, setChartRevenueLoading] = useState(false);

  // Lazy-load below-fold sections
  const belowFoldRef = useRef(null);
  const [belowFoldVisible, setBelowFoldVisible] = useState(false);

  // Per-status revenue data (when chart status filters are active)
  const [statusRevenueData, setStatusRevenueData] = useState({});
  const [statusRevenueSummaries, setStatusRevenueSummaries] = useState({});
  const [statusRevenueLoading, setStatusRevenueLoading] = useState(false);

  // API data states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [revenueData, setRevenueData] = useState([]);
  const [revenueSummary, setRevenueSummary] = useState({ current: 0, previous: 0, change: 0 });
  // Refunds made in the range (docs/refunds.md: counted when made) from
  // /overview/all, used when the revenue response doesn't carry them.
  const [overviewRefunds, setOverviewRefunds] = useState({ refunds: null, netTakings: null });
  const [tipsData, setTipsData] = useState([]);
  const [tipsSummary, setTipsSummary] = useState({ total: 0, change: 0 });
  const [metricsData, setMetricsData] = useState({ newClients: { value: 0, change: 0, previous: 0 }, avgSpend: { value: 0, change: 0, previous: 0 } });
  const [paymentSegments, setPaymentSegments] = useState([]);
  const [paymentsData, setPaymentsData] = useState([]);
  const [outstandingLiabilities, setOutstandingLiabilities] = useState({ total: 0, vouchers: 0, deposits: 0 });
  const [lostRevenue, setLostRevenue] = useState({ total: 0, totalCollected: 0, change: 0, noShows: { count: 0, amount: 0, collected: 0 }, lateCancellations: { count: 0, amount: 0, collected: 0 } });
  const [appointmentsData, setAppointmentsData] = useState({ value: 0, change: 0, chartData: [] });
  const [rebookingData, setRebookingData] = useState({ value: 0, change: 0, chartData: [] });
  const [performanceMetrics, setPerformanceMetrics] = useState({
    staffUtilization: { value: 0, change: 0, subtitle: 'Occupancy rate' },
    clientRetention: { value: 0, change: 0, subtitle: 'Returned within 90 days' },
    retailAttachRate: { value: 0, change: 0, subtitle: '0 product sales' },
    averageTicket: { value: 0, change: 0, subtitle: 'Per appointment' }
  });
  const [vouchersData, setVouchersData] = useState({
    sold: { total: 0, change: 0, count: 0, breakdown: [] },
    redeemed: { total: 0, change: 0, count: 0, breakdown: [] }
  });
  // Discount codes + prices lowered by hand (beu-node-v2 finance getDiscountSummary); null = none / not sent.
  const [discountData, setDiscountData] = useState(null);
  const [loyaltyData, setLoyaltyData] = useState({
    redeemed: { total: 0, count: 0, funding: { salon: { count: 0, total: 0 }, stylist: { count: 0, total: 0 } }, breakdown: [] },
    byStaff: [],
    liability: { bankedRewards: 0, clientsWithRewards: 0, clientsInProgress: 0 }
  });
  const [newClientsList, setNewClientsList] = useState([]);
  // By location: only fetched when the header filter spans more than one site.
  const [bySite, setBySite] = useState(null);
  const [topClientsList, setTopClientsList] = useState([]);

  // Get salonId from localStorage
  const salonId = localStorage.getItem('salonId');

  // Fetch all data
  const fetchData = useCallback(async () => {
    if (!salonId) {
      setError('No salon selected');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    // Everything on this page follows the chart's filter bar (the top one).
    // Custom range when chartDateFilter is null, otherwise the preset.
    const { startDate, endDate } = chartDateFilter === null
      ? { startDate: chartDateStart, endDate: chartDateEnd }
      : getDateRangeFromPreset(chartDateFilter);

    // Get booking status values for the filter
    const selectedStatus = bookingStatusOptions.find(opt => opt.id === bookingStatusFilter);
    const bookingStatuses = selectedStatus?.statuses || undefined;

    const params = {
      salonId,
      ...(salonIds ? { salonIds } : {}),
      startDate: formatDateForApi(startDate),
      endDate: formatDateForApi(endDate),
      granularity: chartGranularity,
      compareWith: chartCompareFilter !== 'none' ? chartCompareFilter : undefined,
      bookingStatus: bookingStatuses ? bookingStatuses.join(',') : undefined,
      hairdresserId: staffFilter !== 'all' ? staffFilter : undefined,
      type: chartTypeFilter.length > 0 ? chartTypeFilter.join(',') : undefined,
      paymentMethod: chartPaymentMethodFilter.length > 0 ? chartPaymentMethodFilter.join(',') : undefined
    };

    try {
      // Fetch all data in parallel - use optimized getOverviewAll for new metrics
      const [revenueRes, tipsRes, metricsRes, paymentRes, overviewAllRes] = await Promise.all([
        financeService.getRevenueOverview(params).catch(err => ({ data: { data: null } })),
        financeService.getTipsCollected(params).catch(err => ({ data: { data: null } })),
        financeService.getMetrics(params).catch(err => ({ data: { data: null } })),
        financeService.getPaymentBreakdown(params).catch(err => ({ data: { data: null } })),
        financeService.getOverviewAll(params).catch(err => ({ data: { data: null } }))
      ]);

      // Process revenue data
      if (revenueRes?.data?.data) {
        const revenue = revenueRes.data.data;
        // Backend returns { current: [...], previous: [...] }
        // Map current array to chart data format
        const currentData = Array.isArray(revenue.current) ? revenue.current : [];
        const previousData = Array.isArray(revenue.previous) ? revenue.previous : [];

        // Calculate totals from the data arrays
        const totalRevenue = currentData.reduce((sum, d) => sum + (d.revenue || 0), 0);
        const previousRevenue = previousData.reduce((sum, d) => sum + (d.revenue || 0), 0);
        // Tax collected inside that revenue (docs/tax-money-path.md §5). 0 for
        // every salon with tax off, so the line below only appears when it applies.
        const totalTax = currentData.reduce((sum, d) => sum + (d.taxTotal || 0), 0);
        // Tax on refunds made in the range: the sale's tax stays in its own
        // month, the refunded part lands here (docs/refunds.md).
        const totalRefundTax = currentData.reduce((sum, d) => sum + (Number(d.refundTax) || 0), 0);

        // Calculate change percentage
        // If previous is 0 and current > 0, show null (will display as "New" or similar)
        // If both are 0, show 0%
        // Otherwise calculate normal percentage change
        let changePercent = 0;
        if (previousRevenue > 0) {
          changePercent = ((totalRevenue - previousRevenue) / previousRevenue) * 100;
        } else if (totalRevenue > 0) {
          changePercent = null; // No previous data to compare - will be handled in display
        }
        
        // Create a map of previous data by date for easy lookup
        const previousMap = {};
        previousData.forEach(d => {
          previousMap[d.date] = d.revenue || 0;
        });
        
        // Format chart data - combine current and previous into single array
        // The chart expects: { day: 'label', current: value, previous: value }
        const chartData = currentData.map((d, index) => {
          // Format date for display (e.g., "Jan 15" or "16:00" for hourly)
          const dayLabel = formatChartLabel(d.date);

          return {
            day: dayLabel,
            date: d.date,
            current: d.revenue || 0,
            previous: previousData[index]?.revenue || 0
          };
        });
        
        setRevenueData(chartData);
        setRevenueSummary({
          current: totalRevenue,
          previous: previousRevenue,
          change: changePercent,
          tax: totalTax,
          refundTax: Math.round(totalRefundTax * 100) / 100
        });
      }

      // Process tips data
      // Backend now returns: { staffTips: [...], total, previous, change }
      if (tipsRes?.data?.data) {
        const tips = tipsRes.data.data;
        // Tips data now has staffTips array and summary fields
        const staffTips = Array.isArray(tips.staffTips) ? tips.staffTips : (Array.isArray(tips) ? tips : []);
        setTipsData(staffTips.map(t => ({
          id: t.hairdresserId,
          name: t.name || 'Unknown',
          initials: t.initials || (t.name ? t.name.split(' ').map(n => n[0]).join('') : '?'),
          totalTips: t.totalTips || 0,
          tipCount: t.tipCount || 0,
          averageTip: t.averageTip || 0
        })));
        // Use the summary from backend (includes comparison data)
        setTipsSummary({
          total: tips.total || staffTips.reduce((sum, t) => sum + (t.totalTips || 0), 0),
          previous: tips.previous || 0,
          change: tips.change || 0
        });
      }

      // Process metrics data
      // Backend now returns: { newClients, avgSpendPerClient, previousNewClients, previousAvgSpend, newClientsChange, avgSpendChange, newClientsChartData, avgSpendChartData }
      if (metricsRes?.data?.data) {
        const metrics = metricsRes.data.data;
        setMetricsData({
          newClients: {
            value: metrics.newClients || 0,
            change: metrics.newClientsChange || 0,
            previous: metrics.previousNewClients || 0,
            chartData: metrics.newClientsChartData || []
          },
          avgSpend: {
            value: metrics.avgSpendPerClient || 0,
            change: metrics.avgSpendChange || 0,
            previous: metrics.previousAvgSpend || 0,
            chartData: metrics.avgSpendChartData || []
          },
          totalClients: metrics.totalClients || 0,
          totalRevenue: metrics.totalRevenue || 0,
          totalTransactions: metrics.totalTransactions || 0,
          totalTips: metrics.totalTips || 0
        });
      }

      // Process payment breakdown
      // Backend returns: [{ paymentType, paymentName, total, count, percentage }]
      if (paymentRes?.data?.data) {
        const payments = paymentRes.data.data;
        const formattedPayments = Array.isArray(payments) ? payments.map(p => ({
          percent: p.percentage || p.percent || 0,
          color: getPaymentColor(p.paymentName || p.method),
          label: p.paymentName || p.method || 'Other',
          total: p.total || 0,
          count: p.count || 0,
          // Refunds paid back by this method in the range (absent = none).
          refunds: rowRefunds(p)
        })) : [];
        setPaymentSegments(formattedPayments);
        setPaymentsData(formattedPayments);
      }

      // Process overview all data (optimized single call for new metrics)
      if (overviewAllRes?.data?.data) {
        const overview = overviewAllRes.data.data;

        setOverviewRefunds({
          refunds: firstRefundTotals(overview.refunds),
          netTakings: overview.netTakings != null ? overview.netTakings : null
        });

        // Outstanding Liabilities (API returns outstandingLiabilities)
        if (overview.outstandingLiabilities) {
          setOutstandingLiabilities({
            total: overview.outstandingLiabilities.total || 0,
            vouchers: overview.outstandingLiabilities.vouchers || 0,
            deposits: overview.outstandingLiabilities.deposits || 0
          });
        }

        // Lost Revenue
        if (overview.lostRevenue) {
          setLostRevenue({
            total: overview.lostRevenue.total || 0,
            totalCollected: overview.lostRevenue.totalCollected || 0,
            change: overview.lostRevenue.change || 0,
            noShows: {
              count: overview.lostRevenue.noShows?.count || 0,
              amount: overview.lostRevenue.noShows?.amount || 0,
              collected: overview.lostRevenue.noShows?.collected || 0
            },
            lateCancellations: {
              count: overview.lostRevenue.lateCancellations?.count || 0,
              amount: overview.lostRevenue.lateCancellations?.amount || 0,
              collected: overview.lostRevenue.lateCancellations?.collected || 0
            }
          });
        }

        // Appointments Data (API returns value, not total)
        if (overview.appointments) {
          setAppointmentsData({
            value: overview.appointments.value || overview.appointments.total || 0,
            change: overview.appointments.change || 0,
            chartData: (overview.appointments.chartData || []).map(d => ({
              label: d.label || d.date || '',
              value: d.value || d.count || 0
            }))
          });
        }

        // Rebooking Rate (API returns value, not rate)
        if (overview.rebookingRate) {
          setRebookingData({
            value: overview.rebookingRate.value || overview.rebookingRate.rate || 0,
            change: overview.rebookingRate.change || 0,
            chartData: (overview.rebookingRate.chartData || []).map(d => ({
              label: d.label || d.date || '',
              value: d.value || d.rate || 0
            }))
          });
        }

        // Performance Metrics (API returns performanceMetrics with nested structure)
        if (overview.performanceMetrics) {
          setPerformanceMetrics({
            staffUtilization: {
              value: overview.performanceMetrics.staffUtilization?.value || 0,
              change: overview.performanceMetrics.staffUtilization?.change || 0,
              subtitle: overview.performanceMetrics.staffUtilization?.subtitle || 'Occupancy rate'
            },
            clientRetention: {
              value: overview.performanceMetrics.clientRetention?.value || 0,
              change: overview.performanceMetrics.clientRetention?.change || 0,
              subtitle: overview.performanceMetrics.clientRetention?.subtitle || 'Returned within 90 days'
            },
            retailAttachRate: {
              value: overview.performanceMetrics.retailAttachRate?.value || 0,
              change: overview.performanceMetrics.retailAttachRate?.change || 0,
              subtitle: overview.performanceMetrics.retailAttachRate?.subtitle || '0 product sales'
            },
            averageTicket: {
              value: overview.performanceMetrics.averageTicket?.value || 0,
              change: overview.performanceMetrics.averageTicket?.change || 0,
              subtitle: overview.performanceMetrics.averageTicket?.subtitle || 'Per appointment'
            }
          });
        }

        // Vouchers Data
        if (overview.vouchers) {
          setVouchersData({
            sold: {
              total: overview.vouchers.sold?.total || 0,
              change: overview.vouchers.sold?.change || 0,
              count: overview.vouchers.sold?.count || 0,
              breakdown: overview.vouchers.sold?.breakdown || []
            },
            redeemed: {
              total: overview.vouchers.redeemed?.total || 0,
              change: overview.vouchers.redeemed?.change || 0,
              count: overview.vouchers.redeemed?.count || 0,
              breakdown: overview.vouchers.redeemed?.breakdown || []
            }
          });
        }

        setDiscountData(overview.discounts && overview.discounts.total > 0 ? overview.discounts : null);

        // Loyalty Data (rewards redeemed at-a-glance + banked-reward liability)
        if (overview.loyalty) {
          setLoyaltyData({
            redeemed: {
              total: overview.loyalty.redeemed?.total || 0,
              count: overview.loyalty.redeemed?.count || 0,
              funding: {
                salon: {
                  count: overview.loyalty.redeemed?.funding?.salon?.count || 0,
                  total: overview.loyalty.redeemed?.funding?.salon?.total || 0
                },
                stylist: {
                  count: overview.loyalty.redeemed?.funding?.stylist?.count || 0,
                  total: overview.loyalty.redeemed?.funding?.stylist?.total || 0
                }
              },
              breakdown: overview.loyalty.redeemed?.breakdown || []
            },
            byStaff: overview.loyalty.byStaff || [],
            liability: {
              bankedRewards: overview.loyalty.liability?.bankedRewards || 0,
              clientsWithRewards: overview.loyalty.liability?.clientsWithRewards || 0,
              clientsInProgress: overview.loyalty.liability?.clientsInProgress || 0
            }
          });
        }

        // New Clients List (API returns newClientsList directly as array)
        if (overview.newClientsList && Array.isArray(overview.newClientsList)) {
          setNewClientsList(overview.newClientsList.map(c => ({
            name: c.name || 'Unknown',
            source: c.source || 'Unknown',
            date: c.date || '' // Already formatted from backend
          })));
        }

        // Top Clients List (API returns topClientsList directly as array)
        if (overview.topClientsList && Array.isArray(overview.topClientsList)) {
          setTopClientsList(overview.topClientsList.map(c => ({
            name: c.name || 'Unknown',
            email: c.email || '',
            spend: c.spend || c.totalSpend || 0
          })));
        }
      }

    } catch (err) {
      console.error('Failed to fetch overview data:', err);
      setError('Failed to load finance data');
    } finally {
      setLoading(false);
    }
  }, [salonId, salonIds, chartDateFilter, chartDateStart, chartDateEnd, chartGranularity, chartCompareFilter, bookingStatusFilter, staffFilter, chartTypeFilter, chartPaymentMethodFilter]);

  // By location: one row per selected site, from the same aggregation each
  // site's own Overview uses — so the table always reconciles with what you
  // see after switching to that site. Only fetched when the filter spans more
  // than one location.
  useEffect(() => {
    if (!salonIds) { setBySite(null); return undefined; }
    let alive = true;
    const { startDate, endDate } = chartDateFilter === null
      ? { startDate: chartDateStart, endDate: chartDateEnd }
      : getDateRangeFromPreset(chartDateFilter);
    financeService.getRevenueBySite({
      salonId,
      salonIds,
      startDate,
      endDate,
      hairdresserId: staffFilter !== 'all' ? staffFilter : undefined
    })
      .then((res) => { if (alive) setBySite((res && res.data && res.data.data) || null); })
      .catch(() => { if (alive) setBySite(null); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salonId, salonIds, chartDateFilter, chartDateStart, chartDateEnd, staffFilter]);

  // Pin the chart filter pills under the fixed header once the page
  // scrolls past them (mobile only; the header's measured bottom keeps
  // this correct with the app's safe-area offset).
  useEffect(() => {
    if (width > 768) { setChartBarSticky(false); return undefined; }
    const onScroll = () => {
      const el = chartBarRef.current;
      if (!el) return;
      const header = document.querySelector('.catalogue-mobile-header') || document.querySelector('.top_header');
      const headerBottom = header ? header.getBoundingClientRect().bottom : 57;
      setChartBarStickyTop(headerBottom);
      setChartBarSticky(el.getBoundingClientRect().top < headerBottom);
    };
    window.addEventListener('scroll', onScroll, true);
    onScroll();
    return () => window.removeEventListener('scroll', onScroll, true);
  }, [width]);

  // Lazy-load: only fetch below-fold data when user scrolls near it
  useEffect(() => {
    const el = belowFoldRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setBelowFoldVisible(true); },
      { rootMargin: '200px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (belowFoldVisible) fetchData();
  }, [fetchData, belowFoldVisible]);

  // Fetch chart revenue independently using chart-specific dates
  const fetchChartRevenue = useCallback(async () => {
    if (!salonId) return;
    setChartRevenueLoading(true);

    const { startDate, endDate } = chartDateFilter === null
      ? { startDate: chartDateStart, endDate: chartDateEnd }
      : getDateRangeFromPreset(chartDateFilter);

    const selectedStatus = bookingStatusOptions.find(opt => opt.id === bookingStatusFilter);
    const bookingStatuses = selectedStatus?.statuses || undefined;

    const params = {
      salonId,
      ...(salonIds ? { salonIds } : {}),
      startDate: formatDateForApi(startDate),
      endDate: formatDateForApi(endDate),
      granularity: chartGranularity,
      compareWith: chartCompareFilter !== 'none' ? chartCompareFilter : undefined,
      bookingStatus: bookingStatuses ? bookingStatuses.join(',') : undefined,
      hairdresserId: staffFilter !== 'all' ? staffFilter : undefined,
      type: chartTypeFilter.length > 0 ? chartTypeFilter.join(',') : undefined,
      paymentMethod: chartPaymentMethodFilter.length > 0 ? chartPaymentMethodFilter.join(',') : undefined
    };

    try {
      const revenueRes = await financeService.getRevenueOverview(params).catch(() => ({ data: { data: null } }));

      if (revenueRes?.data?.data) {
        const revenue = revenueRes.data.data;
        const currentData = Array.isArray(revenue.current) ? revenue.current : [];
        const previousData = Array.isArray(revenue.previous) ? revenue.previous : [];

        const totalRevenue = currentData.reduce((sum, d) => sum + (d.revenue || 0), 0);
        const totalSalonRevenue = currentData.reduce((sum, d) => sum + (d.salonRevenue || 0), 0);
        const totalStylistRevenue = currentData.reduce((sum, d) => sum + (d.stylistRevenue || 0), 0);
        const totalPipelineRevenue = currentData.reduce((sum, d) => sum + (d.pipelineRevenue || 0), 0);
        const totalPipelineCount = currentData.reduce((sum, d) => sum + (d.pipelineCount || 0), 0);
        const totalLoyaltyComps = currentData.reduce((sum, d) => sum + (d.loyaltyComps || 0), 0);
        const previousRevenue = previousData.reduce((sum, d) => sum + (d.revenue || 0), 0);
        // Refunds in the range: { count, amount } on the response, else per-day
        // `refunds` summed. Absent = none (the line stays hidden).
        // Daily rows carry refundSalesAmount (off takings, tips excluded) and refundCount.
        const dayRefunds = currentData.reduce((sum, d) => sum + Math.abs(Number(d.refundSalesAmount != null ? d.refundSalesAmount : d.refundAmount) || 0), 0);
        const dayRefundCount = currentData.reduce((sum, d) => sum + (Number(d.refundCount) || 0), 0);
        const chartRefunds = firstRefundTotals(revenue.refunds) || (dayRefunds > 0 ? { amount: Math.round(dayRefunds * 100) / 100, count: dayRefundCount || null } : null);

        let changePercent = 0;
        if (previousRevenue > 0) {
          changePercent = ((totalRevenue - previousRevenue) / previousRevenue) * 100;
        } else if (totalRevenue > 0) {
          changePercent = null;
        }

        const chartData = currentData.map((d, index) => {
          return {
            day: formatChartLabel(d.date),
            date: d.date,
            current: d.revenue || 0,
            previous: previousData[index]?.revenue || 0
          };
        });

        setChartRevenueData(chartData);
        setChartRevenueSummary({
          current: totalRevenue,
          previous: previousRevenue,
          change: changePercent,
          salonRevenue: totalSalonRevenue,
          stylistRevenue: totalStylistRevenue,
          pipelineRevenue: totalPipelineRevenue,
          pipelineCount: totalPipelineCount,
          loyaltyComps: totalLoyaltyComps,
          refunds: chartRefunds,
          netTakings: revenue.netTakings != null ? revenue.netTakings : null
        });
      }
    } catch (err) {
      console.error('Failed to fetch chart revenue data:', err);
    } finally {
      setChartRevenueLoading(false);
    }
  }, [salonId, salonIds, chartDateFilter, chartDateStart, chartDateEnd, chartGranularity, chartCompareFilter, bookingStatusFilter, staffFilter, chartTypeFilter, chartPaymentMethodFilter]);

  useEffect(() => {
    fetchChartRevenue();
  }, [fetchChartRevenue]);

  // Fetch per-status revenue data when chart status filters are active
  useEffect(() => {
    if (chartStatusFilter.length === 0) {
      setStatusRevenueData({});
      setStatusRevenueSummaries({});
      return;
    }

    const fetchStatusRevenue = async () => {
      if (!salonId) return;
      setStatusRevenueLoading(true);

      const { startDate, endDate } = chartDateFilter === null
        ? { startDate: chartDateStart, endDate: chartDateEnd }
        : getDateRangeFromPreset(chartDateFilter);

      const baseParams = {
        salonId,
        ...(salonIds ? { salonIds } : {}),
      ...(salonIds ? { salonIds } : {}),
        startDate: formatDateForApi(startDate),
        endDate: formatDateForApi(endDate),
        granularity: chartGranularity,
        compareWith: chartCompareFilter !== 'none' ? chartCompareFilter : undefined,
        hairdresserId: staffFilter !== 'all' ? staffFilter : undefined,
        type: chartTypeFilter.length > 0 ? chartTypeFilter.join(',') : undefined,
        paymentMethod: chartPaymentMethodFilter.length > 0 ? chartPaymentMethodFilter.join(',') : undefined,
      };

      try {
        const promises = chartStatusFilter.map(async (statusId) => {
          const config = chartStatusOptions.find(o => o.id === statusId);
          if (!config) return null;
          const params = { ...baseParams, bookingStatus: config.bookingStatus };
          const res = await financeService.getRevenueOverview(params).catch(() => ({ data: { data: null } }));
          const revenue = res?.data?.data;
          if (!revenue) return { statusId, data: [], total: 0, previous: 0, change: 0 };

          const currentData = Array.isArray(revenue.current) ? revenue.current : [];
          const previousData = Array.isArray(revenue.previous) ? revenue.previous : [];
          const totalRevenue = currentData.reduce((sum, d) => sum + (d.revenue || 0), 0);
          const totalSalon = currentData.reduce((sum, d) => sum + (d.salonRevenue || 0), 0);
          const totalStylist = currentData.reduce((sum, d) => sum + (d.stylistRevenue || 0), 0);
          const previousRevenue = previousData.reduce((sum, d) => sum + (d.revenue || 0), 0);
          let changePercent = 0;
          if (previousRevenue > 0) {
            changePercent = ((totalRevenue - previousRevenue) / previousRevenue) * 100;
          } else if (totalRevenue > 0) {
            changePercent = null;
          }

          return {
            statusId,
            data: currentData.map(d => ({ date: d.date, revenue: d.revenue || 0 })),
            previousData: previousData.map((d, i) => ({ date: currentData[i]?.date || d.date, revenue: d.revenue || 0 })),
            total: totalRevenue,
            salonRevenue: totalSalon,
            stylistRevenue: totalStylist,
            previous: previousRevenue,
            change: changePercent,
          };
        });

        const results = (await Promise.all(promises)).filter(Boolean);

        // Build per-status chart data keyed by statusId
        const dataByStatus = {};
        const summariesByStatus = {};
        let combinedSalonRevenue = 0;
        let combinedStylistRevenue = 0;
        let combinedTotal = 0;
        results.forEach(r => {
          dataByStatus[r.statusId] = r.data;
          dataByStatus[r.statusId + '_previous'] = r.previousData || [];
          summariesByStatus[r.statusId] = { current: r.total, previous: r.previous, change: r.change };
          combinedSalonRevenue += r.salonRevenue || 0;
          combinedStylistRevenue += r.stylistRevenue || 0;
          combinedTotal += r.total || 0;
        });

        setStatusRevenueData(dataByStatus);
        setStatusRevenueSummaries(summariesByStatus);
        // Update chart summary with SE breakdown from per-status data
        setChartRevenueSummary(prev => ({
          ...prev,
          current: combinedTotal,
          salonRevenue: combinedSalonRevenue,
          stylistRevenue: combinedStylistRevenue
        }));
      } catch (err) {
        console.error('Failed to fetch per-status revenue data:', err);
      } finally {
        setStatusRevenueLoading(false);
      }
    };

    fetchStatusRevenue();
  }, [salonId, salonIds, chartDateFilter, chartDateStart, chartDateEnd, chartGranularity, chartCompareFilter, staffFilter, chartStatusFilter, chartTypeFilter, chartPaymentMethodFilter]);


  // Chart data for mini charts (derived from metrics - use backend data if available)
  const newClientsChartData = (metricsData.newClients?.chartData && metricsData.newClients.chartData.length > 0)
    ? metricsData.newClients.chartData
    : [{ label: '', value: 0 }, { label: '', value: 0 }, { label: '', value: 0 }, { label: '', value: 0 }];

  const spendPerClientChartData = (metricsData.avgSpend?.chartData && metricsData.avgSpend.chartData.length > 0)
    ? metricsData.avgSpend.chartData
    : [{ label: '', value: 0 }, { label: '', value: 0 }, { label: '', value: 0 }, { label: '', value: 0 }];

  // Format currency
  // Drill-down from the By location table: the active site opens straight
  // away, another site switches this device first (per-site caches are keyed
  // to the site, so the app reloads on it).
  const openSiteFinance = (id) => {
    if (String(id) === String(salonId)) { window.location.href = '/finance'; return; }
    switchToSite(id, '/finance');
  };

  const siteCell = { padding: '10px 12px', borderBottom: '1px solid #f0f0f0', whiteSpace: 'nowrap', color: '#1a1a1a' };
  const numCell = { ...siteCell, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };
  const totalCell = { padding: '10px 12px', borderTop: '2px solid #eee', fontWeight: 600, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' };

  const formatCurrency = (value) => {
    return `£${Number(value || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Format change percentage
  const formatChange = (value) => {
    if (value === null || value === undefined) {
      return 'New'; // No previous data to compare
    }
    const num = Number(value);
    if (isNaN(num)) return 'New';
    return num >= 0 ? `+${num.toFixed(1)}%` : `${num.toFixed(1)}%`;
  };

  // Count active chart filters
  const chartFilterCount = chartStatusFilter.length + chartTypeFilter.length + chartPaymentMethodFilter.length;

  // Daily / Weekly. In the top row on desktop; on phones it sits with the
  // chart legend, so the top row stays one clean line of chips.
  const renderGranularity = (overlays) => (
    <div style={{ position: 'relative', flexShrink: 0 }}>
      <button
        onClick={() => setChartGranularityOpen(!chartGranularityOpen)}
        style={{
          display: 'flex', alignItems: 'center', gap: '4px',
          height: '32px', padding: width <= 768 ? '0 10px' : '0 12px',
          backgroundColor: 'white', border: '1px solid #e5e5e5',
          borderRadius: '9999px', fontSize: width <= 768 ? '12px' : '13px',
          color: '#007EA0', fontWeight: '500',
          cursor: 'pointer', whiteSpace: 'nowrap'
        }}
      >
        <span>{chartGranularity === 'daily' ? 'Daily' : 'Weekly'}</span>
        <ChevronDownIcon size={12} />
      </button>
      {overlays && chartGranularityOpen && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 99 }} onClick={() => setChartGranularityOpen(false)} />
          <div style={{
            position: 'absolute', top: '100%', ...(width <= 768 ? { right: 0 } : { left: 0 }), marginTop: '6px',
            backgroundColor: 'white', borderRadius: '10px',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.12)',
            border: '1px solid #e5e5e5', zIndex: 100,
            minWidth: '120px', overflow: 'hidden'
          }}>
            {[{ id: 'daily', label: 'Daily' }, { id: 'weekly', label: 'Weekly' }].map((option) => (
              <button
                key={option.id}
                onClick={() => { setChartGranularity(option.id); setChartGranularityOpen(false); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: '10px',
                  width: '100%', padding: '10px 14px', border: 'none',
                  backgroundColor: chartGranularity === option.id ? '#f0f9ff' : 'white',
                  color: chartGranularity === option.id ? '#007EA0' : '#1a1a1a',
                  fontSize: '13px',
                  fontWeight: chartGranularity === option.id ? '500' : '400',
                  textAlign: 'left', cursor: 'pointer'
                }}
              >
                {chartGranularity === option.id && <CheckIcon size={14} />}
                {chartGranularity !== option.id && <div style={{ width: '14px' }} />}
                {option.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );

  // The chart filter pills, rendered both inline in the card and (on
  // mobile, once scrolled past) pinned in a fixed strip under the header.
  // Pickers and menus render only from the interactive copy (overlays).
  const renderChartFilterBar = (overlays, bare = false) => (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: width <= 768 ? '6px' : '8px',
            flexWrap: 'wrap',
            ...(bare ? {} : {
              marginBottom: '12px',
              paddingBottom: '12px',
              borderBottom: '1px solid #f0f0f0'
            })
          }}>
            {/* Date Range Button */}
            <div style={{ position: 'relative', flexShrink: 0 }}>
              <button
                onClick={() => setChartDatePickerOpen(!chartDatePickerOpen)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '4px',
                  height: '32px', padding: width <= 768 ? '0 10px' : '0 12px',
                  backgroundColor: 'white', border: '1px solid #e5e5e5',
                  borderRadius: '9999px', fontSize: width <= 768 ? '12px' : '13px', color: '#737373',
                  cursor: 'pointer', whiteSpace: 'nowrap'
                }}
              >
                {width > 768 && (
                  <>
                    <span>Date range</span>
                    <div style={{ height: '16px', width: '1px', backgroundColor: '#e5e5e5', margin: '0 6px' }} />
                  </>
                )}
                <span style={{ color: '#007EA0', fontWeight: '500' }}>
                  {chartDateFilter === null && chartDateStart && chartDateEnd
                    ? (width <= 768 && chartDateStart.getMonth() === chartDateEnd.getMonth() && chartDateStart.getFullYear() === chartDateEnd.getFullYear()
                      ? `${chartDateStart.getDate()}-${chartDateEnd.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`
                      : `${chartDateStart.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} - ${chartDateEnd.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`)
                    : getDatePresetLabel(chartDateFilter)
                  }
                </span>
                <ChevronDownIcon size={12} />
              </button>
              <DateRangePicker
                isOpen={overlays && chartDatePickerOpen}
                onClose={() => setChartDatePickerOpen(false)}
                startDate={chartDateStart}
                endDate={chartDateEnd}
                selectedPreset={chartDateFilter}
                onPresetChange={setChartDateFilter}
                onApply={(start, end, presetId) => { setChartDateStart(start); setChartDateEnd(end); setChartDateFilter(presetId || null); }}
              />
            </div>

            {width > 768 && renderGranularity(overlays)}

            {/* Compare Dropdown */}
            {chartCompareFilter !== 'none' ? (
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                <button
                  onClick={() => setChartCompareOpen(!chartCompareOpen)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '4px',
                    height: '32px', padding: width <= 768 ? '0 10px' : '0 12px',
                    backgroundColor: 'white', border: '1px solid #e5e5e5',
                    borderRadius: '9999px', fontSize: width <= 768 ? '12px' : '13px', color: '#737373',
                    cursor: 'pointer', whiteSpace: 'nowrap'
                  }}
                >
                  {width > 768 && (
                    <span
                      onClick={(e) => { e.stopPropagation(); setChartCompareFilter('none'); }}
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2px', cursor: 'pointer', marginRight: '4px' }}
                    >
                      <CloseIcon size={12} />
                    </span>
                  )}
                  {width > 768 && (
                    <>
                      <span>Compare</span>
                      <div style={{ height: '16px', width: '1px', backgroundColor: '#e5e5e5', margin: '0 6px' }} />
                    </>
                  )}
                  <span style={{ color: '#007EA0', fontWeight: '500' }}>
                    {width <= 768
                      ? (chartCompareFilter === 'previous' ? 'vs previous' : 'vs last year')
                      : (chartCompareFilter === 'previous' ? 'Previous period' : 'Last year')}
                  </span>
                  <ChevronDownIcon size={12} />
                </button>
                {overlays && chartCompareOpen && (
                  <>
                    <div style={{ position: 'fixed', inset: 0, zIndex: 99 }} onClick={() => setChartCompareOpen(false)} />
                    <div style={{
                      position: 'absolute', top: '100%', left: 0, marginTop: '6px',
                      backgroundColor: 'white', borderRadius: '10px',
                      boxShadow: '0 10px 40px rgba(0, 0, 0, 0.12)',
                      border: '1px solid #e5e5e5', zIndex: 100,
                      minWidth: '180px', overflow: 'hidden'
                    }}>
                      {[
                        { id: 'previous', label: 'Previous period' },
                        { id: 'year', label: 'Same period last year' },
                        { id: 'none', label: 'No comparison' }
                      ].map((option) => (
                        <button
                          key={option.id}
                          onClick={() => { setChartCompareFilter(option.id); setChartCompareOpen(false); }}
                          style={{
                            display: 'flex', alignItems: 'center', gap: '10px',
                            width: '100%', padding: '10px 14px', border: 'none',
                            backgroundColor: chartCompareFilter === option.id ? '#f0f9ff' : 'white',
                            color: chartCompareFilter === option.id ? '#007EA0' : '#1a1a1a',
                            fontSize: '13px',
                            fontWeight: chartCompareFilter === option.id ? '500' : '400',
                            textAlign: 'left', cursor: 'pointer'
                          }}
                        >
                          {chartCompareFilter === option.id && <CheckIcon size={14} />}
                          {chartCompareFilter !== option.id && <div style={{ width: '14px' }} />}
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            ) : (
              <button
                onClick={() => setChartCompareFilter('previous')}
                style={{
                  display: 'flex', alignItems: 'center', height: '32px',
                  padding: '0 12px', border: '1px dashed #d1d5db',
                  borderRadius: '9999px', background: 'white',
                  cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap'
                }}
              >
                <PlusIcon size={12} />
                <span style={{ color: '#737373', fontSize: width <= 768 ? '12px' : '13px', marginLeft: '4px' }}>Compare</span>
              </button>
            )}
          </div>
  );

  // Error state
  if (error) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '400px' }}>
        <div style={{ textAlign: 'center', padding: '24px' }}>
          <div style={{ width: '48px', height: '48px', borderRadius: '50%', backgroundColor: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
            <svg width="24" height="24" fill="none" stroke="#ef4444" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
          </div>
          <p style={{ color: '#1a1a1a', fontSize: '16px', fontWeight: '500', marginBottom: '8px' }}>Failed to load data</p>
          <p style={{ color: '#737373', fontSize: '14px', marginBottom: '16px' }}>{error}</p>
          <button onClick={fetchData} style={{ padding: '8px 16px', backgroundColor: '#007EA0', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', cursor: 'pointer' }}>Try again</button>
        </div>
      </div>
    );
  }

  // Skeleton building block
  const Skeleton = ({ width = '100%', height = 16, borderRadius = 6, style = {} }) => (
    <div className="skeleton-block" style={{ width, height, borderRadius, ...style }} />
  );

  // Line chart skeleton SVG
  const ChartLineSkeleton = () => (
    <svg width="100%" height="100%" viewBox="0 0 600 220" preserveAspectRatio="none">
      <defs>
        <linearGradient id="skelShimmer" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#f0f0f0" />
          <stop offset="50%" stopColor="#e0e0e0" />
          <stop offset="100%" stopColor="#f0f0f0" />
          <animate attributeName="x1" values="-1;1" dur="1.5s" repeatCount="indefinite" />
          <animate attributeName="x2" values="0;2" dur="1.5s" repeatCount="indefinite" />
        </linearGradient>
      </defs>
      <path d="M0,160 C50,150 100,120 150,100 C200,80 250,50 300,60 C350,70 400,40 450,55 C500,70 550,80 600,70 L600,200 L0,200 Z" fill="url(#skelShimmer)" opacity="0.4" />
      <path d="M0,160 C50,150 100,120 150,100 C200,80 250,50 300,60 C350,70 400,40 450,55 C500,70 550,80 600,70" fill="none" stroke="url(#skelShimmer)" strokeWidth="2.5" />
      <path d="M0,170 C50,165 100,150 150,140 C200,130 250,110 300,120 C350,130 400,100 450,115 C500,130 550,140 600,135" fill="none" stroke="#ebebeb" strokeWidth="2" strokeDasharray="6 4" />
      {[0, 75, 150, 225, 300, 375, 450, 525, 600].map((x, i) => (
        <rect key={`x${i}`} x={x - 12} y={208} width={24} height={8} rx={4} fill="#f0f0f0" />
      ))}
      {[40, 90, 140, 190].map((y, i) => (
        <rect key={`y${i}`} x={0} y={y - 4} width={20} height={8} rx={4} fill="#f0f0f0" />
      ))}
    </svg>
  );

  // Card skeleton for below-fold sections
  const CardSkeleton = ({ rows = 4, showHeader = true }) => (
    <Card>
      {showHeader && (
        <div style={{ marginBottom: 16 }}>
          <Skeleton width={160} height={20} style={{ marginBottom: 8 }} />
          <Skeleton width={100} height={12} />
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Skeleton width={36} height={36} borderRadius={18} />
            <div style={{ flex: 1 }}>
              <Skeleton width={`${60 + (i % 3) * 15}%`} height={14} style={{ marginBottom: 6 }} />
              <Skeleton width={`${40 + (i % 2) * 20}%`} height={10} />
            </div>
            <Skeleton width={60} height={14} />
          </div>
        ))}
      </div>
    </Card>
  );

  // Metrics row skeleton (for payment breakdown, metrics grid)
  const MetricsGridSkeleton = ({ cols = 3 }) => (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 24 }}>
      {Array.from({ length: cols }).map((_, i) => (
        <Card key={i}>
          <Skeleton width={100} height={14} style={{ marginBottom: 12 }} />
          <Skeleton width={80} height={28} style={{ marginBottom: 8 }} />
          <Skeleton width={60} height={12} />
        </Card>
      ))}
    </div>
  );

  // Below-fold full skeleton layout
  const BelowFoldSkeleton = () => (
    <>
      {/* AI Insights skeleton */}
      <Card>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
          <Skeleton width={20} height={20} borderRadius={10} />
          <Skeleton width={100} height={16} />
        </div>
        <Skeleton width="90%" height={14} style={{ marginBottom: 8 }} />
        <Skeleton width="75%" height={14} style={{ marginBottom: 8 }} />
        <Skeleton width="60%" height={14} />
      </Card>


      {/* Metrics grid skeleton */}
      <MetricsGridSkeleton cols={isMobile ? 1 : 3} />
    </>
  );

  return (
    <div>
      {width <= 768 && chartBarSticky && (
        <div style={{
          position: 'fixed',
          top: chartBarStickyTop,
          left: 0,
          right: 0,
          zIndex: 98,
          backgroundColor: '#fff',
          padding: '8px 16px',
          borderBottom: '1px solid #e5e5e5',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)'
        }}>
          {renderChartFilterBar(true, true)}
        </div>
      )}

      {/* Gross Revenue Card */}
      <div style={{ marginBottom: '24px' }}>
        <Card>
          <CardHeader
            title={
              chartStatusFilter.length === 0
                ? 'Gross revenue'
                : chartStatusFilter.length === 1
                  ? `Gross revenue — ${chartStatusOptions.find(o => o.id === chartStatusFilter[0])?.label || 'Bookings'}`
                  : 'Gross revenue by status'
            }
            tooltip="Total revenue generated from all bookings, services, products, and vouchers during the selected period"
            action={
              <div style={{ position: 'relative' }}>
                <button
                  onClick={() => setChartFiltersOpen(!chartFiltersOpen)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    height: '36px',
                    padding: '0 14px',
                    backgroundColor: chartFilterCount > 0 ? '#f0f9ff' : 'white',
                    border: chartFilterCount > 0 ? '1px solid #007EA0' : '1px solid #e5e5e5',
                    borderRadius: '9999px',
                    fontSize: '14px',
                    color: chartFilterCount > 0 ? '#007EA0' : '#4b5563',
                    cursor: 'pointer',
                    transition: 'all 0.15s'
                  }}
                >
                  <FilterIcon size={14} />
                  <span style={{ fontWeight: '500' }}>Filters</span>
                  {chartFilterCount > 0 && (
                    <span style={{
                      backgroundColor: '#007EA0',
                      color: 'white',
                      fontSize: '11px',
                      fontWeight: '600',
                      padding: '2px 7px',
                      borderRadius: '10px',
                      minWidth: '18px',
                      textAlign: 'center'
                    }}>
                      {chartFilterCount}
                    </span>
                  )}
                </button>

                {chartFiltersOpen && (() => {
                  const mobileSheet = width <= 768;
                  const pill = (active) => ({
                    padding: mobileSheet ? '8px 14px' : '6px 12px',
                    borderRadius: mobileSheet ? '999px' : '6px',
                    fontSize: '13px',
                    fontWeight: '500',
                    border: active ? (mobileSheet ? 'none' : '1px solid #007EA0') : '1px solid #e5e5e5',
                    backgroundColor: active ? (mobileSheet ? '#29282D' : '#f0f9ff') : 'white',
                    color: active ? (mobileSheet ? 'white' : '#007EA0') : '#4b5563',
                    cursor: 'pointer',
                    transition: 'all 0.15s'
                  });
                  const sectionLabel = { fontSize: '12px', fontWeight: '600', color: '#737373', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px', marginTop: 0 };
                  const sections = (
                    <>
                      <div style={{ marginBottom: '16px' }}>
                        <p style={sectionLabel}>Booking Status</p>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                          {chartStatusOptions.map((option) => (
                            <button
                              key={option.id}
                              onClick={() => setChartStatusFilter(prev => prev.includes(option.id) ? prev.filter(v => v !== option.id) : [...prev, option.id])}
                              style={pill(chartStatusFilter.includes(option.id))}
                            >
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: option.color }} />
                                {option.label}
                              </span>
                            </button>
                          ))}
                        </div>
                      </div>
                      <div style={{ marginBottom: '16px' }}>
                        <p style={sectionLabel}>Type</p>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                          {chartTypeOptions.map((option) => (
                            <button
                              key={option.id}
                              onClick={() => setChartTypeFilter(prev => prev.includes(option.id) ? prev.filter(v => v !== option.id) : [...prev, option.id])}
                              style={pill(chartTypeFilter.includes(option.id))}
                            >
                              {option.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div style={{ marginBottom: '16px' }}>
                        <p style={sectionLabel}>Payment Method</p>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                          {chartPaymentMethodOptions.map((option) => (
                            <button
                              key={option.id}
                              onClick={() => setChartPaymentMethodFilter(prev => prev.includes(option.id) ? prev.filter(v => v !== option.id) : [...prev, option.id])}
                              style={pill(chartPaymentMethodFilter.includes(option.id))}
                            >
                              {option.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </>
                  );
                  const resetAll = () => { setChartStatusFilter([]); setChartTypeFilter([]); setChartPaymentMethodFilter([]); };
                  return (
                    <>
                      <div
                        className={mobileSheet ? 'sheet-anim-backdrop' : undefined}
                        style={{ position: 'fixed', inset: 0, zIndex: 199, backgroundColor: mobileSheet ? 'rgba(0,0,0,0.4)' : 'transparent' }}
                        onClick={() => setChartFiltersOpen(false)}
                      />
                      {mobileSheet ? (
                        <div className="sheet-anim-up" style={{
                          position: 'fixed',
                          bottom: 0,
                          left: 0,
                          right: 0,
                          backgroundColor: 'white',
                          borderRadius: '16px 16px 0 0',
                          boxShadow: '0 -4px 40px rgba(0, 0, 0, 0.15)',
                          zIndex: 200,
                          maxHeight: '80vh',
                          display: 'flex',
                          flexDirection: 'column'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #e5e5e5', flexShrink: 0 }}>
                            <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1a1a1a', margin: 0 }}>Filters</h3>
                            <button
                              onClick={() => setChartFiltersOpen(false)}
                              style={{ width: '32px', height: '32px', borderRadius: '50%', border: 'none', backgroundColor: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '16px', color: '#1a1a1a' }}
                            >
                              &#x2715;
                            </button>
                          </div>
                          <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px 0' }}>
                            {sections}
                          </div>
                          <div style={{ padding: '0 20px', paddingBottom: 'calc(16px + env(safe-area-inset-bottom))', flexShrink: 0 }}>
                            <div style={{ display: 'flex', gap: '8px', paddingTop: '12px', borderTop: '1px solid #e5e5e5' }}>
                              <button
                                onClick={resetAll}
                                style={{ flex: 1, padding: '14px', border: '1px solid #e5e5e5', borderRadius: '10px', backgroundColor: 'white', color: '#737373', fontSize: '15px', fontWeight: '500', cursor: 'pointer' }}
                              >
                                Reset all
                              </button>
                              <button
                                onClick={() => setChartFiltersOpen(false)}
                                style={{ flex: 1, padding: '14px', border: 'none', borderRadius: '10px', backgroundColor: '#29282D', color: 'white', fontSize: '15px', fontWeight: '600', cursor: 'pointer' }}
                              >
                                Done
                              </button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="sheet-anim-pop" style={{
                          position: 'absolute',
                          top: '100%',
                          right: 0,
                          marginTop: '8px',
                          backgroundColor: 'white',
                          borderRadius: '12px',
                          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
                          border: '1px solid #e5e5e5',
                          zIndex: 200,
                          width: '360px',
                          padding: '16px'
                        }}>
                          {sections}
                          <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '12px', borderTop: '1px solid #e5e5e5' }}>
                            <button
                              onClick={resetAll}
                              style={{ padding: '8px 16px', border: 'none', borderRadius: '6px', backgroundColor: 'transparent', color: '#737373', fontSize: '13px', fontWeight: '500', cursor: 'pointer' }}
                            >
                              Reset all
                            </button>
                            <button
                              onClick={() => setChartFiltersOpen(false)}
                              style={{ padding: '8px 20px', border: 'none', borderRadius: '6px', backgroundColor: '#007EA0', color: 'white', fontSize: '13px', fontWeight: '500', cursor: 'pointer' }}
                            >
                              Done
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            }
          />

          <div ref={chartBarRef}>{renderChartFilterBar(!chartBarSticky)}</div>

          {/* Active chart filter chips */}
          {chartFilterCount > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '16px' }}>
              {chartStatusFilter.map(status => {
                const opt = chartStatusOptions.find(o => o.id === status);
                return (
                  <span key={`cs-${status}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '4px 10px', backgroundColor: '#f0f9ff', border: '1px solid #007EA0', borderRadius: '6px', fontSize: '12px', color: '#007EA0', fontWeight: '500' }}>
                    <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: opt?.color || '#007EA0' }} />
                    {opt?.label}
                    <button onClick={() => setChartStatusFilter(prev => prev.filter(v => v !== status))} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0', display: 'flex', alignItems: 'center' }}>
                      <svg style={{ width: '12px', height: '12px', color: '#007EA0' }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                  </span>
                );
              })}
              {chartTypeFilter.map(typeId => {
                const opt = chartTypeOptions.find(o => o.id === typeId);
                return (
                  <span key={`ct-${typeId}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '4px 10px', backgroundColor: '#f0f9ff', border: '1px solid #007EA0', borderRadius: '6px', fontSize: '12px', color: '#007EA0', fontWeight: '500' }}>
                    {opt?.label}
                    <button onClick={() => setChartTypeFilter(prev => prev.filter(v => v !== typeId))} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0', display: 'flex', alignItems: 'center' }}>
                      <svg style={{ width: '12px', height: '12px', color: '#007EA0' }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                  </span>
                );
              })}
              {chartPaymentMethodFilter.map(pmId => {
                const opt = chartPaymentMethodOptions.find(o => o.id === pmId);
                return (
                  <span key={`cp-${pmId}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '4px 10px', backgroundColor: '#f0f9ff', border: '1px solid #007EA0', borderRadius: '6px', fontSize: '12px', color: '#007EA0', fontWeight: '500' }}>
                    {opt?.label}
                    <button onClick={() => setChartPaymentMethodFilter(prev => prev.filter(v => v !== pmId))} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0', display: 'flex', alignItems: 'center' }}>
                      <svg style={{ width: '12px', height: '12px', color: '#007EA0' }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                  </span>
                );
              })}
              <button onClick={() => { setChartStatusFilter([]); setChartTypeFilter([]); setChartPaymentMethodFilter([]); }} style={{ padding: '4px 8px', border: 'none', borderRadius: '6px', backgroundColor: 'transparent', color: '#737373', fontSize: '12px', fontWeight: '500', cursor: 'pointer', textDecoration: 'underline' }}>
                Clear all
              </button>
            </div>
          )}

          {/* Metrics section */}
          {chartStatusFilter.length === 0 ? (
            /* Default: single summary */
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '16px' }}>
              {chartRevenueLoading ? (
                <div>
                  <Skeleton width={120} height={32} style={{ marginBottom: 8 }} />
                  <Skeleton width={160} height={14} />
                </div>
              ) : (
                <div>
                  {/* Phones: one column. Desktop: the total with the split
                      beside it, then the extras as one strip underneath. */}
                  {(() => {
                    const refundsInRange = chartRevenueSummary.refunds || overviewRefunds.refunds;
                    const breakdownRows = [
                      chartRevenueSummary.pipelineRevenue > 0 && {
                        label: 'Unactioned',
                        note: chartRevenueSummary.pipelineCount > 0 ? `${chartRevenueSummary.pipelineCount} ${chartRevenueSummary.pipelineCount === 1 ? 'booking' : 'bookings'}` : null,
                        value: `+${formatCurrency(chartRevenueSummary.pipelineRevenue)}`,
                        help: "Bookings in this period that haven't been checked out yet. Includes upcoming appointments and past appointments that need actioning (no payment captured)."
                      },
                      chartRevenueSummary.loyaltyComps > 0 && {
                        label: 'Loyalty rewards',
                        value: `−${formatCurrency(chartRevenueSummary.loyaltyComps)}`,
                        colour: '#ec4899',
                        help: 'Value of services given away as loyalty rewards this period. Comped, not collected: the revenue above is gross and includes their notional value. Commission still runs on the full value.'
                      },
                      chartRevenueSummary.loyaltyComps > 0 && {
                        label: 'Net collected',
                        value: formatCurrency((chartRevenueSummary.current || 0) - (chartRevenueSummary.loyaltyComps || 0)),
                        help: 'Gross revenue minus loyalty rewards.'
                      },
                      refundsInRange && {
                        label: 'Refunds',
                        note: refundCountLabel(refundsInRange.count) || null,
                        value: minusMoney(refundsInRange.amount),
                        colour: '#b42318',
                        help: 'Money paid back to clients in this period, counted on the day it was refunded. The sales stay in their own period.'
                      },
                      refundsInRange && {
                        label: 'Net takings',
                        value: formatCurrency(netAfterRefunds(chartRevenueSummary.current, refundsInRange, chartRevenueSummary.netTakings != null ? chartRevenueSummary.netTakings : overviewRefunds.netTakings)),
                        help: 'Sales in this period minus refunds made in it.'
                      },
                      revenueSummary.tax > 0 && {
                        label: 'Includes tax',
                        value: formatCurrency(revenueSummary.tax),
                        help: 'Tax collected inside the gross revenue above.'
                      },
                      revenueSummary.refundTax > 0 && {
                        label: 'Tax refunded',
                        value: minusMoney(revenueSummary.refundTax),
                        help: 'Tax inside the refunds made in this period. It comes off the tax due for this period.'
                      }
                    ].filter(Boolean);
                    const total = (
                      <MetricValue value={formatCurrency(chartRevenueSummary.current)} change={formatChange(chartRevenueSummary.change)} changeType={chartRevenueSummary.change === null ? 'neutral' : (chartRevenueSummary.change >= 0 ? 'positive' : 'negative')} subtitle={chartRevenueSummary.previous > 0 ? `${formatCurrency(chartRevenueSummary.previous)} previous period` : 'No previous period data'} />
                    );
                    const splitCards = hasSEStaff && (
                      <RevenueSplitCards
                        summary={chartRevenueSummary}
                        stylistName={staffFilter !== 'all' && isFilteredStaffSE ? filteredStaffInfo?.name : null}
                        formatCurrency={formatCurrency}
                        inline={!isMobile}
                      />
                    );
                    if (isMobile) {
                      return <>{total}<RevenueBreakdown rows={breakdownRows} />{splitCards}</>;
                    }
                    return (
                      <>
                        <div style={{ display: 'grid', gridTemplateColumns: splitCards ? 'minmax(0, 1fr) minmax(0, 1fr)' : '1fr', gap: '32px', alignItems: 'start' }}>
                          {total}
                          {splitCards}
                        </div>
                        <RevenueBreakdown rows={breakdownRows} layout="strip" />
                      </>
                    );
                  })()}
                </div>
              )}
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '16px', fontSize: '12px', color: '#737373' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><span style={{ width: '12px', height: '2px', backgroundColor: '#007EA0', borderRadius: '9999px' }} />Current period</span>
                {chartCompareFilter !== 'none' && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><span style={{ width: '12px', height: '2px', backgroundColor: '#e5e5e5', borderRadius: '9999px' }} />{chartCompareFilter === 'year' ? 'Last year' : 'Previous period'}</span>
                )}
                {width <= 768 && <div style={{ marginLeft: 'auto' }}>{renderGranularity(true)}</div>}
              </div>
            </div>
          ) : (
            /* Per-status summaries */
            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', marginBottom: '12px' }}>
                {chartStatusFilter.map(statusId => {
                  const config = chartStatusOptions.find(o => o.id === statusId);
                  const summary = statusRevenueSummaries[statusId];
                  if (!config) return null;
                  return (
                    <div key={statusId} style={{ flex: '1 1 140px', minWidth: '140px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                        <span style={{ width: '12px', height: '3px', backgroundColor: config.color, borderRadius: '9999px', display: 'inline-block' }} />
                        <span style={{ fontSize: '12px', color: '#737373' }}>{config.label}</span>
                      </div>
                      <p style={{ fontSize: '20px', fontWeight: '600', color: '#1a1a1a', margin: 0 }}>
                        {summary ? formatCurrency(summary.current) : '£0.00'}
                      </p>
                      {summary && summary.change !== null && summary.change !== undefined && (
                        <p style={{ fontSize: '12px', color: summary.change >= 0 ? '#059669' : '#ef4444', margin: '2px 0 0 0' }}>
                          {formatChange(summary.change)}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '16px', fontSize: '12px', color: '#737373' }}>
                {chartStatusFilter.map(statusId => {
                  const config = chartStatusOptions.find(o => o.id === statusId);
                  if (!config) return null;
                  return (
                    <span key={statusId} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ width: '12px', height: '2px', backgroundColor: config.color, borderRadius: '9999px' }} />
                      {config.label}
                    </span>
                  );
                })}
              </div>
              {/* SE Revenue Breakdown toggle - per-status mode */}
              {hasSEStaff && !statusRevenueLoading && (
                <RevenueSplitCards
                  summary={chartRevenueSummary}
                  stylistName={staffFilter !== 'all' && isFilteredStaffSE ? filteredStaffInfo?.name : null}
                  formatCurrency={formatCurrency}
                />
              )}
            </div>
          )}

          {/* Chart */}
          <div style={{ height: '256px' }}>
            {chartRevenueLoading && chartStatusFilter.length === 0 ? (
              <ChartLineSkeleton />
            ) : chartStatusFilter.length === 0 ? (
              /* Default chart - current/previous */
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartRevenueData}>
                  <defs>
                    <linearGradient id="grossGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#007EA0" stopOpacity={0.1}/>
                      <stop offset="100%" stopColor="#007EA0" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{ fill: '#737373', fontSize: 12 }} dy={10} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fill: '#737373', fontSize: 12 }} tickFormatter={(val) => val >= 1000 ? `£${val / 1000}k` : `£${val}`} dx={-10} />
                  <Tooltip content={<CustomTooltip />} cursor={{ stroke: '#007EA0', strokeWidth: 1, strokeDasharray: '4 4' }} />
                  <Area type="monotone" dataKey="previous" stroke="#e5e5e5" strokeWidth={2} fill="none" dot={false} />
                  <Area type="monotone" dataKey="current" stroke="#007EA0" strokeWidth={2} fill="url(#grossGradient)" dot={false} activeDot={{ r: 6, fill: '#007EA0', stroke: '#fff', strokeWidth: 2 }} />
                </AreaChart>
              </ResponsiveContainer>
            ) : statusRevenueLoading ? (
              <ChartLineSkeleton />
            ) : (
              /* Per-status chart */
              (() => {
                // Merge per-status data into a single chart data array
                const dateSet = new Set();
                chartStatusFilter.forEach(statusId => {
                  const data = statusRevenueData[statusId] || [];
                  data.forEach(d => dateSet.add(d.date));
                });
                const allDates = [...dateSet].sort();

                if (allDates.length === 0) {
                  return (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                      <p style={{ fontSize: '14px', color: '#737373' }}>No revenue data for this period</p>
                    </div>
                  );
                }

                const mergedData = allDates.map((date, index) => {
                  const point = { day: formatChartLabel(date), previous: 0 };
                  chartStatusFilter.forEach(statusId => {
                    const data = statusRevenueData[statusId] || [];
                    const match = data.find(d => d.date === date);
                    point[statusId] = match ? match.revenue : 0;
                    // Sum previous period data across all selected statuses
                    const prevData = statusRevenueData[statusId + '_previous'] || [];
                    const prevMatch = prevData[index];
                    point.previous += prevMatch ? prevMatch.revenue : 0;
                  });
                  return point;
                });

                return (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={mergedData}>
                      <defs>
                        {chartStatusFilter.map(statusId => {
                          const config = chartStatusOptions.find(o => o.id === statusId);
                          if (!config) return null;
                          return (
                            <linearGradient key={statusId} id={`statusGradient-${statusId}`} x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor={config.color} stopOpacity={0.08} />
                              <stop offset="100%" stopColor={config.color} stopOpacity={0} />
                            </linearGradient>
                          );
                        })}
                      </defs>
                      <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{ fill: '#737373', fontSize: 12 }} dy={10} />
                      <YAxis axisLine={false} tickLine={false} tick={{ fill: '#737373', fontSize: 12 }} tickFormatter={(val) => val >= 1000 ? `£${val / 1000}k` : `£${val}`} dx={-10} />
                      <Tooltip content={<CustomTooltip />} cursor={{ stroke: '#999', strokeWidth: 1, strokeDasharray: '4 4' }} />
                      <Area type="monotone" dataKey="previous" stroke="#e5e5e5" strokeWidth={2} fill="none" dot={false} name="Previous" />
                      {chartStatusFilter.map(statusId => {
                        const config = chartStatusOptions.find(o => o.id === statusId);
                        if (!config) return null;
                        return (
                          <Area
                            key={statusId}
                            type="monotone"
                            dataKey={statusId}
                            stroke={config.color}
                            strokeWidth={2}
                            fill={`url(#statusGradient-${statusId})`}
                            dot={false}
                            activeDot={{ r: 6, fill: config.color, stroke: '#fff', strokeWidth: 2 }}
                            name={config.label}
                          />
                        );
                      })}
                    </AreaChart>
                  </ResponsiveContainer>
                );
              })()
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e5e5e5' }}>
            <span style={{ fontSize: '12px', color: '#a3a3a3' }}>Updated 4 seconds ago</span>
          </div>
        </Card>
      </div>

      <div ref={belowFoldRef} style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
        {/* AI Insights */}
        {(!belowFoldVisible || loading) && <BelowFoldSkeleton />}
        {belowFoldVisible && !loading && (
          <AIInsightsCard
            revenueSummary={revenueSummary}
            revenueData={revenueData}
            performanceMetrics={performanceMetrics}
            rebookingData={rebookingData}
            lostRevenue={lostRevenue}
            metricsData={metricsData}
            appointmentsData={appointmentsData}
            tipsData={tipsData}
          />
        )}

        {bySite && bySite.multi && Array.isArray(bySite.sites) && bySite.sites.length > 1 && (() => {
          // Refunds made at each site in the period (hidden when none).
          const siteRefunds = !!(bySite.total && bySite.total.refunds > 0);
          return (
          <Card>
            <CardHeader title="By location" tooltip="Each location's own figures for this period. Click a row to open that location's finance." />
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                <thead>
                  <tr>
                    {['Location', 'Revenue', ...(siteRefunds ? ['Refunds', 'Net takings'] : []), 'Services', 'Products', 'Tips', 'Sales', 'Avg ticket'].map((h, i) => (
                      <th key={h} style={{ padding: '10px 12px', textAlign: i === 0 ? 'left' : 'right', borderBottom: '1px solid #f0f0f0', fontSize: '11px', letterSpacing: '0.06em', textTransform: 'uppercase', color: '#737373', fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {bySite.sites.map((row) => (
                    <tr key={row.salonId} onClick={() => openSiteFinance(row.salonId)} style={{ cursor: 'pointer' }} title={`Open ${row.name}'s finance`}>
                      <td style={siteCell}><span style={{ display: 'inline-block', width: '9px', height: '9px', borderRadius: '999px', marginRight: '8px', verticalAlign: 'middle', background: siteColour(row.salonId) }} />{row.name}</td>
                      <td style={numCell}>{formatCurrency(row.revenue)}</td>
                      {siteRefunds && <td style={{ ...numCell, color: row.refunds > 0 ? '#b42318' : '#9ca3af' }}>{row.refunds > 0 ? minusMoney(row.refunds) : formatCurrency(0)}</td>}
                      {siteRefunds && <td style={numCell}>{formatCurrency(row.netRevenue != null ? row.netRevenue : (row.revenue || 0) - (row.refunds || 0))}</td>}
                      <td style={numCell}>{formatCurrency(row.serviceRevenue)}</td>
                      <td style={numCell}>{formatCurrency(row.productRevenue)}</td>
                      <td style={numCell}>{formatCurrency(row.tips)}</td>
                      <td style={numCell}>{row.transactions}</td>
                      <td style={numCell}>{formatCurrency(row.avgTicket)}</td>
                    </tr>
                  ))}
                  <tr>
                    <td style={totalCell}>Total</td>
                    <td style={{ ...totalCell, textAlign: 'right' }}>{formatCurrency(bySite.total.revenue)}</td>
                    {siteRefunds && <td style={{ ...totalCell, textAlign: 'right', color: '#b42318' }}>{minusMoney(bySite.total.refunds)}</td>}
                    {siteRefunds && <td style={{ ...totalCell, textAlign: 'right' }}>{formatCurrency(bySite.total.netRevenue != null ? bySite.total.netRevenue : (bySite.total.revenue || 0) - (bySite.total.refunds || 0))}</td>}
                    <td style={{ ...totalCell, textAlign: 'right' }}>{formatCurrency(bySite.total.serviceRevenue)}</td>
                    <td style={{ ...totalCell, textAlign: 'right' }}>{formatCurrency(bySite.total.productRevenue)}</td>
                    <td style={{ ...totalCell, textAlign: 'right' }}>{formatCurrency(bySite.total.tips)}</td>
                    <td style={{ ...totalCell, textAlign: 'right' }}>{bySite.total.transactions}</td>
                    <td style={{ ...totalCell, textAlign: 'right' }}>{formatCurrency(bySite.total.avgTicket)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </Card>
          );
        })()}

        {belowFoldVisible && !loading && <>
        {/* Row 3: Payments + Outstanding Liabilities + Lost Revenue */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '24px' }} className="payments-row">
          {/* Payments Card */}
          <Card>
            <CardHeader title="Payments" tooltip="Breakdown of payment methods used by customers, including card, cash, and other payment types" />
            <div style={{ marginBottom: '16px' }}>
              <StackedProgressBar segments={paymentSegments} />
            </div>
            <div style={{ borderTop: '1px solid #f0f0f0' }}>
              {paymentsData.map((p, idx) => (
                <LegendItem
                  key={idx}
                  color={p.color}
                  label={p.refunds ? <>{p.label}<span style={{ color: '#9ca3af' }}> · {minusMoney(p.refunds.amount)} refunded</span></> : p.label}
                  value={formatCurrency(p.total)}
                />
              ))}
            </div>
          </Card>

          {/* Outstanding Liabilities Card */}
          <Card>
            <CardHeader title="Outstanding liabilities" tooltip="Total amount of unpaid vouchers and commissions that are yet to be settled or redeemed" />
            <MetricValue
              value={formatCurrency(outstandingLiabilities.total)}
              subtitle="Unredeemed balance"
            />
            <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #f0f0f0' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '14px', marginBottom: '12px' }}>
                <span style={{ color: '#737373' }}>Vouchers</span>
                <span style={{ fontWeight: '500', color: '#1a1a1a' }}>{formatCurrency(outstandingLiabilities.vouchers)}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '14px' }}>
                <span style={{ color: '#737373' }}>Deposits held</span>
                <span style={{ fontWeight: '500', color: '#1a1a1a' }}>{formatCurrency(outstandingLiabilities.deposits)}</span>
              </div>
            </div>
          </Card>

          {/* Lost Revenue Card */}
          <Card>
            <CardHeader title="Lost revenue" tooltip="Net revenue lost from no-shows and late cancellations, after subtracting any fees collected from the client" />
            <MetricValue
              value={formatCurrency(lostRevenue.total)}
              change={`+£${lostRevenue.change} vs previous`}
              changeType="negative"
              subtitle="This period"
            />
            {lostRevenue.totalCollected > 0 && (
              <p style={{ fontSize: '12px', color: '#059669', margin: '4px 0 0 0' }}>
                {formatCurrency(lostRevenue.totalCollected)} recovered from fees
              </p>
            )}
            <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #f0f0f0' }}>
              <div style={{ marginBottom: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '14px' }}>
                  <span style={{ color: '#737373' }}>No-shows</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ padding: '2px 6px', backgroundColor: '#fef2f2', color: '#ef4444', borderRadius: '4px', fontSize: '12px', fontWeight: '500' }}>{lostRevenue.noShows.count}</span>
                    <span style={{ fontWeight: '500', color: '#1a1a1a' }}>{formatCurrency(lostRevenue.noShows.amount)}</span>
                  </div>
                </div>
                {lostRevenue.noShows.collected > 0 && (
                  <p style={{ fontSize: '12px', color: '#059669', margin: '4px 0 0 0', textAlign: 'right' }}>
                    {formatCurrency(lostRevenue.noShows.collected)} collected
                  </p>
                )}
              </div>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '14px' }}>
                  <span style={{ color: '#737373' }}>Late cancellations</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ padding: '2px 6px', backgroundColor: '#fef2f2', color: '#ef4444', borderRadius: '4px', fontSize: '12px', fontWeight: '500' }}>{lostRevenue.lateCancellations.count}</span>
                    <span style={{ fontWeight: '500', color: '#1a1a1a' }}>{formatCurrency(lostRevenue.lateCancellations.amount)}</span>
                  </div>
                </div>
                {lostRevenue.lateCancellations.collected > 0 && (
                  <p style={{ fontSize: '12px', color: '#059669', margin: '4px 0 0 0', textAlign: 'right' }}>
                    {formatCurrency(lostRevenue.lateCancellations.collected)} collected
                  </p>
                )}
              </div>
            </div>
          </Card>
        </div>

        {/* Row 4: New Clients + Spend per Client (with charts) */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '24px' }} className="metrics-row">
          <Card>
            <CardHeader title="New clients" tooltip="Number of first-time customers who booked and completed their first appointment during the selected period" />
            <MetricValue
              value={String(metricsData.newClients.value)}
              change={metricsData.newClients.change >= 0 ? `+${Math.round(metricsData.newClients.change)}%` : `${Math.round(metricsData.newClients.change)}%`}
              changeType={metricsData.newClients.change >= 0 ? 'positive' : 'negative'}
            />
            <MiniLineChart data={newClientsChartData} color="#007EA0" height={120} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px' }}>
              <span style={{ fontSize: '12px', color: '#9ca3af' }}>Updated 2 hours ago</span>
            </div>
          </Card>
          <Card>
            <CardHeader title="Spend per client" tooltip="Average amount spent per customer visit, including services, products, and tips" />
            <MetricValue
              value={formatCurrency(metricsData.avgSpend.value)}
              change={metricsData.avgSpend.change >= 0 ? `+${Math.round(metricsData.avgSpend.change)}%` : `${Math.round(metricsData.avgSpend.change)}%`}
              changeType={metricsData.avgSpend.change >= 0 ? 'positive' : 'negative'}
            />
            <MiniLineChart data={spendPerClientChartData} color="#007EA0" height={120} valuePrefix="£" />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px' }}>
              <span style={{ fontSize: '12px', color: '#9ca3af' }}>Updated 17 seconds ago</span>
            </div>
          </Card>
        </div>

        {/* Row 5: Appointments + Rebooking Rate (with charts) */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '24px' }} className="metrics-row">
          <Card>
            <CardHeader title="Transactions" tooltip="Total number of completed transactions during the selected period, including all services and treatments" />
            <MetricValue
              value={String(appointmentsData.value)}
              change={appointmentsData.change >= 0 ? `+${appointmentsData.change}%` : `${appointmentsData.change}%`}
              changeType={appointmentsData.change >= 0 ? 'positive' : 'negative'}
            />
            <MiniLineChart data={appointmentsData.chartData} color="#007EA0" height={120} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px' }}>
              <span style={{ fontSize: '12px', color: '#9ca3af' }}>Updated 5 minutes ago</span>
            </div>
          </Card>
          <Card>
            <CardHeader title="Rebooking rate" tooltip="Percentage of completed appointments where the client booked again within 48 hours" />
            <MetricValue
              value={`${rebookingData.value}%`}
              change={rebookingData.change >= 0 ? `+${rebookingData.change}%` : `${rebookingData.change}%`}
              changeType={rebookingData.change >= 0 ? 'positive' : 'negative'}
            />
            <MiniLineChart data={rebookingData.chartData} color="#007EA0" height={120} valueSuffix="%" />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px' }}>
              <span style={{ fontSize: '12px', color: '#9ca3af' }}>Updated 1 hour ago</span>
            </div>
          </Card>
        </div>

        {/* Row 6: Performance Metrics (4 smaller cards) */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '24px' }} className="performance-row">
          <Card>
            <CardHeader title="Staff utilization" tooltip="Percentage of available staff time that was booked with client appointments" />
            <MetricValue
              value={`${performanceMetrics.staffUtilization.value}%`}
              change={performanceMetrics.staffUtilization.change >= 0 ? `+${performanceMetrics.staffUtilization.change}%` : `${performanceMetrics.staffUtilization.change}%`}
              changeType={performanceMetrics.staffUtilization.change >= 0 ? 'positive' : 'negative'}
              subtitle={performanceMetrics.staffUtilization.subtitle}
            />
          </Card>
          <Card>
            <CardHeader title="Client retention" tooltip="Percentage of clients who returned for at least one more appointment within the last 90 days" />
            <MetricValue
              value={`${performanceMetrics.clientRetention.value}%`}
              change={performanceMetrics.clientRetention.change >= 0 ? `+${performanceMetrics.clientRetention.change}%` : `${performanceMetrics.clientRetention.change}%`}
              changeType={performanceMetrics.clientRetention.change >= 0 ? 'positive' : 'negative'}
              subtitle={performanceMetrics.clientRetention.subtitle}
            />
          </Card>
          <Card>
            <CardHeader title="Retail attach rate" tooltip="Percentage of appointments that included a retail product purchase, showing effectiveness of product recommendations" />
            <MetricValue
              value={`${performanceMetrics.retailAttachRate.value}%`}
              change={performanceMetrics.retailAttachRate.change >= 0 ? `+${performanceMetrics.retailAttachRate.change}%` : `${performanceMetrics.retailAttachRate.change}%`}
              changeType={performanceMetrics.retailAttachRate.change >= 0 ? 'positive' : 'negative'}
              subtitle={performanceMetrics.retailAttachRate.subtitle}
            />
          </Card>
          <Card>
            <CardHeader title="Average ticket" tooltip="Average transaction value per appointment, including services, products, and add-ons" />
            <MetricValue
              value={formatCurrency(performanceMetrics.averageTicket.value)}
              change={performanceMetrics.averageTicket.change >= 0 ? `+${formatCurrency(performanceMetrics.averageTicket.change)}` : `-${formatCurrency(Math.abs(performanceMetrics.averageTicket.change))}`}
              changeType={performanceMetrics.averageTicket.change >= 0 ? 'positive' : 'negative'}
              subtitle={performanceMetrics.averageTicket.subtitle}
            />
          </Card>
        </div>

        {/* Row 7: Vouchers Sold + Vouchers Redeemed + Tips */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '24px' }} className="vouchers-row">
          {/* Vouchers Sold */}
          <Card>
            <CardHeader title="Vouchers sold" tooltip="Total value of gift vouchers and packages sold to customers during the selected period" />
            <MetricValue
              value={formatCurrency(vouchersData.sold.total)}
              change={`+${formatCurrency(vouchersData.sold.change)}`}
              changeType="positive"
              subtitle={`${vouchersData.sold.count} vouchers this period`}
            />
            <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #f0f0f0' }}>
              {vouchersData.sold.breakdown.map((item, idx) => (
                <div key={idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '14px', marginBottom: idx < vouchersData.sold.breakdown.length - 1 ? '8px' : 0 }}>
                  <span style={{ color: '#737373' }}>{item.name}</span>
                  <span style={{ fontWeight: '500', color: '#1a1a1a' }}>{item.count} sold</span>
                </div>
              ))}
            </div>
          </Card>

          {/* Vouchers Redeemed */}
          <Card>
            <CardHeader title="Vouchers redeemed" tooltip="Value of vouchers used by customers for services and products during the selected period" />
            <MetricValue
              value={formatCurrency(vouchersData.redeemed.total)}
              change={`+${formatCurrency(vouchersData.redeemed.change)}`}
              changeType="positive"
              subtitle={`${vouchersData.redeemed.count} redemptions this period`}
            />
            <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #f0f0f0' }}>
              {vouchersData.redeemed.breakdown.map((item, idx) => (
                <div key={idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '14px', marginBottom: idx < vouchersData.redeemed.breakdown.length - 1 ? '8px' : 0 }}>
                  <span style={{ color: '#737373' }}>{item.name}</span>
                  <span style={{ fontWeight: '500', color: '#1a1a1a' }}>{item.value ? formatCurrency(item.value) : item.count}</span>
                </div>
              ))}
            </div>
          </Card>

          {/* Tips */}
          <Card>
            <CardHeader title="Tips" tooltip="Total gratuities received by staff members from customers during the selected period" />
            <MetricValue
              value={formatCurrency(tipsSummary.total)}
              change={formatChange(tipsSummary.change)}
              changeType={tipsSummary.change >= 0 ? 'positive' : 'negative'}
              subtitle={`${tipsData.length || 0} tips this period`}
            />
            <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #f0f0f0' }}>
              {(tipsExpanded ? tipsData : tipsData.slice(0, 3)).map((staff) => (
                <div key={staff.name} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '14px', marginBottom: '8px' }}>
                  <span style={{ color: '#737373' }}>{staff.name}</span>
                  <span style={{ fontWeight: '500', color: '#1a1a1a' }}>£{(staff.totalTips || staff.amount || 0).toFixed(2)}</span>
                </div>
              ))}
            </div>
            {tipsData.length > 3 && (
              <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'center' }}>
                <button
                  onClick={() => setTipsExpanded(!tipsExpanded)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    padding: '8px 16px',
                    border: '1px solid #e5e5e5',
                    borderRadius: '9999px',
                    fontSize: '13px',
                    fontWeight: '500',
                    color: '#737373',
                    backgroundColor: 'white',
                    cursor: 'pointer'
                  }}
                >
                  {tipsExpanded ? (
                    <>
                      Show less
                      <svg style={{ width: '14px', height: '14px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                      </svg>
                    </>
                  ) : (
                    <>
                      Show all ({tipsData.length})
                      <svg style={{ width: '14px', height: '14px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </>
                  )}
                </button>
              </div>
            )}
          </Card>
        </div>

        {/* Row 7a: Discounts given — codes that stuck + prices lowered by hand */}
        {discountData && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '24px' }} className="loyalty-row">
            <Card>
              <CardHeader title="Discounts given" tooltip="Money taken off the menu price in the selected period: discount codes used, and prices lowered by hand at the till or checkout. Takings above are already after these." />
              <MetricValue
                value={formatCurrency(discountData.total)}
                subtitle={`${discountData.sales} sale${discountData.sales !== 1 ? 's' : ''} with money off`}
              />
              <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #f0f0f0' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '14px', marginBottom: '12px' }}>
                  <span style={{ color: '#737373' }}>Discount codes ({discountData.codes.count})</span>
                  <span style={{ fontWeight: '500', color: '#1a1a1a' }}>{formatCurrency(discountData.codes.total)}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '14px' }}>
                  <span style={{ color: '#737373' }}>Taken off by hand ({discountData.manual.count})</span>
                  <span style={{ fontWeight: '500', color: '#1a1a1a' }}>{formatCurrency(discountData.manual.total)}</span>
                </div>
              </div>
            </Card>
            <Card>
              <CardHeader title="Who paid for codes" tooltip="What discount codes cost each side. On an employed stylist's service the salon pays it all; on a self-employed stylist's service it follows the code (salon pays, stylist pays, or shared at their commission rate), and is settled in Commissions." />
              <MetricValue
                value={formatCurrency(discountData.codes.salonAbsorbed)}
                subtitle="Cost to the salon"
              />
              <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #f0f0f0' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '14px', marginBottom: '12px' }}>
                  <span style={{ color: '#737373' }}>Salon</span>
                  <span style={{ fontWeight: '500', color: '#1a1a1a' }}>{formatCurrency(discountData.codes.salonAbsorbed)}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '14px' }}>
                  <span style={{ color: '#737373' }}>Self-employed stylists</span>
                  <span style={{ fontWeight: '500', color: '#1a1a1a' }}>{formatCurrency(discountData.codes.stylistAbsorbed)}</span>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Row 7b: Loyalty — rewards redeemed + outstanding liability (at-a-glance) */}
        {(loyaltyData.redeemed.count > 0 || loyaltyData.liability.bankedRewards > 0) && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '24px' }} className="loyalty-row">
            {/* Rewards Redeemed */}
            <Card>
              <CardHeader title="Rewards redeemed" tooltip="Notional value of loyalty rewards redeemed (comped) during the selected period, and how many rewards were used" />
              <MetricValue
                value={formatCurrency(loyaltyData.redeemed.total)}
                subtitle={`${loyaltyData.redeemed.count} reward${loyaltyData.redeemed.count !== 1 ? 's' : ''} redeemed this period`}
              />
              <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #f0f0f0' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '14px', marginBottom: '12px' }}>
                  <span style={{ color: '#737373' }}>Salon-funded ({loyaltyData.redeemed.funding.salon.count})</span>
                  <span style={{ fontWeight: '500', color: '#1a1a1a' }}>{formatCurrency(loyaltyData.redeemed.funding.salon.total)}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '14px' }}>
                  <span style={{ color: '#737373' }}>Stylist-funded ({loyaltyData.redeemed.funding.stylist.count})</span>
                  <span style={{ fontWeight: '500', color: '#1a1a1a' }}>{formatCurrency(loyaltyData.redeemed.funding.stylist.total)}</span>
                </div>
              </div>
            </Card>

            {/* Outstanding Liability */}
            <Card>
              <CardHeader title="Outstanding loyalty liability" tooltip="Rewards clients have banked but not yet redeemed — a future cost owed once they cash them in" />
              <MetricValue
                value={String(loyaltyData.liability.bankedRewards)}
                subtitle="Rewards banked, not yet redeemed"
              />
              <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #f0f0f0' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '14px', marginBottom: '12px' }}>
                  <span style={{ color: '#737373' }}>Clients with a reward ready</span>
                  <span style={{ fontWeight: '500', color: '#1a1a1a' }}>{loyaltyData.liability.clientsWithRewards}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '14px' }}>
                  <span style={{ color: '#737373' }}>Clients in progress</span>
                  <span style={{ fontWeight: '500', color: '#1a1a1a' }}>{loyaltyData.liability.clientsInProgress}</span>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Row 8: New Clients + Top Clients side by side */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '24px' }} className="clients-row">
          {/* New Clients List */}
          <Card>
            <CardHeader title="New clients" tooltip="Number of first-time customers who booked and completed their first appointment during the selected period" />
            <MetricValue
              value={String(metricsData.newClients.value)}
              change={metricsData.newClients.change >= 0 ? `+${Math.round(metricsData.newClients.change)}%` : `${Math.round(metricsData.newClients.change)}%`}
              changeType={metricsData.newClients.change >= 0 ? 'positive' : 'negative'}
              subtitle={`${metricsData.newClients.previous} previous period`}
            />
            <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #f0f0f0' }}>
              {newClientsList.map((client) => (
                <div key={client.name} style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                  <div style={{ width: '24px', height: '24px', borderRadius: '50%', backgroundColor: '#1a1a1a', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: '600' }}>
                    {client.name.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontSize: '14px', fontWeight: '500', color: '#1a1a1a', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{client.name}</p>
                    <p style={{ fontSize: '12px', color: '#a3a3a3', margin: 0 }}>{client.date}</p>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* Top Clients by Spend */}
          <Card>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <h3 style={{ fontWeight: '500', color: '#1a1a1a', margin: 0, fontSize: '15px' }}>Top clients by spend</h3>
              </div>
              <span style={{ fontSize: '14px', color: '#737373' }}>This period</span>
            </div>
            <div>
              {topClientsList.map((client) => (
                <div key={client.name} style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                  <div style={{ width: '24px', height: '24px', borderRadius: '50%', backgroundColor: '#1a1a1a', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: '600' }}>
                    {client.name.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontSize: '14px', fontWeight: '500', color: '#1a1a1a', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{client.name}</p>
                    <p style={{ fontSize: '12px', color: '#a3a3a3', margin: 0 }}>{client.email}</p>
                  </div>
                  <span style={{ fontSize: '14px', fontWeight: '600', color: '#1a1a1a' }}>
                    £{client.spend.toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          </Card>
        </div>
        </>}
      </div>


      {/* Responsive styles */}
      <style>{`
        @media (max-width: 768px) {
          .payments-row,
          .vouchers-row {
            grid-template-columns: 1fr !important;
          }
          .metrics-row,
          .clients-row,
          .loyalty-row {
            grid-template-columns: 1fr !important;
          }
          .performance-row {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }
        @media (min-width: 769px) and (max-width: 1024px) {
          .payments-row,
          .vouchers-row {
            grid-template-columns: repeat(2, 1fr) !important;
          }
          .performance-row {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }
        /* Filter bar row gap for mobile wrap */
        @media (max-width: 768px) {
          .finance-filter-bar {
            row-gap: 0.5rem;
          }
        }
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes skeletonShimmer {
          0% { background-position: -400px 0; }
          100% { background-position: 400px 0; }
        }
        .skeleton-block {
          background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%);
          background-size: 800px 100%;
          animation: skeletonShimmer 1.5s ease-in-out infinite;
          border-radius: 6px;
        }
      `}</style>
    </div>
  );
};

export default OverviewView;